import { createContext, useCallback, useContext, useMemo } from 'react';
import { get, isEqual } from 'lodash';

import { useTrackEvent } from '@/lib/analytics';
import { createBackNavigationToast, useToastContext } from '@/components/toast';

import {
  isCellWithPipeline,
  isCellWithVisualisations,
  isChatCell,
  isConversationCell,
  isInvalidCell,
  isRecordsLikeCell,
  resetCellRollback,
  setViewOption,
  setViewOptions,
  isConversationPart,
  removeViewOptions,
} from '@/core/cell';
import { convertKeysToSnakeCase, convertToYaml } from '@/lib/utils/object';

import {
  deleteExplorationCell,
  getExplorationCellIndex,
  setExplorationCells,
  getCellRow,
  getCellRows,
  updateCellRowCollapsed,
  updateCellRowHeight,
  ensureCellTabs,
} from '@/core/exploration';

import {
  Cell,
  CellViewOptions,
  Exploration,
  Pipeline,
  PipelineOperation,
  Visualisation,
} from '../types';
import { QueryMeta, useExplorationContext } from './exploration-context';
import { useExplorationUIContext } from './exploration-ui-context';
import {
  dereferencePipeline,
  flattenPipeline,
  getPipelineWithRollback,
  countOperations,
} from '../pipeline/utils';
import {
  addOperation,
  addOperations,
  moveOperation,
  removeOperation,
  replaceOperations,
  updateCellPipeline,
  updateOperation,
} from '../edit-pipeline/utils';

import { addCombineToExploration, updateCombineInExploration } from './combine';
import { CombineStep } from './combine/types';

import { generateVisualisation } from '../components/visualisation/utils';

import { useMetadataContext } from '../metadata-context';
import { ensureValidPipeline } from '../utils';
import { extractCellFromExploration } from './utils';

import { restoreInvalidOperations } from '../output';
import { useConversationExplorationContext } from '../conversation/conversation-exploration-context';
import { regenerateVisualisationIfNeeded } from './sidebar/cell-sidebar/utils';

export interface ExplorationCellContextValue {
  cell: Cell;
  cellIndex: number;
  isSelectedCell: boolean;
  isConversationPart: boolean;
  isConversationSubject: boolean;
  rowContainsConversationPart: boolean;
  pipelineWithRollback: Pipeline | null;
  setCell: (cell: Cell) => void;
  setQueryMeta: (meta: QueryMeta) => void;
  setCellViewOption: <T extends keyof CellViewOptions>(
    key: T,
    value: CellViewOptions[T] | undefined,
  ) => void;
  getCellViewOption: <T extends keyof CellViewOptions>(key: T) => CellViewOptions[T]; // Json | undefined;
  isCollapsed: boolean;
  isCollapsible: boolean;
  toggleCollapsed: () => void;
  addVisualization: (visualisation: Visualisation) => void;
  addDefaultVisualization: () => void;
  startConversation: () => void;
  endConversation: () => void;
  setVisualisation: (idx: number, visualisation: Visualisation) => void;
  removeVisualisation: (idx: number) => void;
  isTableVisible: boolean;
  hideTable: () => void;
  showTable: () => void;
  isPipelinePreviewVisible: boolean;
  isBlockTitleVisible: boolean;
  tableMode: 'table' | 'cards';
  getYaml: () => string;
  copyCell: () => void;
  addPipelineOperation: (atIndex: number, operation: PipelineOperation) => void;
  removePipelineOperation: (atIndex: number) => void;
  removeParentPipelineOperation: (atIndex: number) => void;
  updatePipelineOperation: (atIndex: number, operation: PipelineOperation) => void;
  updateParentPipelineOperation: (atIndex: number, operation: PipelineOperation) => void;
  reorderPipelineOperation: (fromIndex: number, toIndex: number) => void;
  disablePipelineOperation: (atIndex: number, disabled: boolean) => void;
  disableParentPipelineOperation: (atIndex: number, disabled: boolean) => void;
  detachPipeline: () => void;
  changeBasePipeline: (newPipeline: Pipeline) => void;
  addCombine: (items: CombineStep[]) => void;
  updateCombine: (items: CombineStep[]) => void;
  setRollbackIndex: (index: number) => void;
  clearRollback: () => void;
}

const defaultContextValue = Symbol();

const ExplorationCellContext = createContext<
  ExplorationCellContextValue | typeof defaultContextValue
>(defaultContextValue);

export const useExplorationCellContext = () => {
  const context = useContext(ExplorationCellContext);
  if (context === defaultContextValue) {
    throw new Error(
      'useExplorationCellContext must be used within a ExplorationCellContextProvider',
    );
  }
  return context;
};

const TableVisibilityKey = 'tableVisibility';
export const TableModeKey = 'viewMode';
export const PipelinePreviewKey = 'pipelinePreviewVisibility';
export const BlockTitleKey = 'blockTitleVisibility' as const;

export const ExplorationCellContextProvider = ({
  cell,
  isCollapsible,
  enforcedRowCollapse,
  children,
}: {
  cell: Cell;
  isCollapsible: boolean;
  enforcedRowCollapse: boolean;
  children: React.ReactNode;
}) => {
  const {
    exploration,
    setCellById,
    setQueryMeta,
    getVariables,
    setExploration,
    persistCopiedCell,
    isConversationSubject,
  } = useExplorationContext();
  const { isWithinConversation } = useConversationExplorationContext();
  const {
    selectedCell,
    selectCell,
    scrollToCell,
    closeEditor,
    setNewConversationSubjectId,
    activeTabId,
  } = useExplorationUIContext();

  const { models, metrics } = useMetadataContext();
  const trackEvent = useTrackEvent();
  const addToast = useToastContext();

  const stateContext = useMemo(
    () => ({
      models,
      variables: getVariables(),
      metrics,
      exploration,
    }),
    [models, getVariables, metrics, exploration],
  );

  const isSelectedCell = selectedCell?.id === cell.id;
  const cellIndex = getExplorationCellIndex(exploration, cell.id);

  const cellIsPartOfConversation = isConversationPart(cell) || isWithinConversation;
  const rowContainsConversationPart = exploration.view.cells.some(
    (c) =>
      c.viewOptions?.rowId !== undefined &&
      c.viewOptions.rowId === cell.viewOptions?.rowId &&
      isConversationPart(c),
  );

  /**
   * Update the exploration with the new pipeline and in certain scenarios regenerate the visualisation
   */
  const handleExplorationUpdate = (updatedExploration: Exploration) => {
    if (
      !isRecordsLikeCell(cell) ||
      cell.pipeline.pipelineId === undefined ||
      cell.visualisations?.length === 0
    ) {
      return setExploration(updatedExploration);
    }

    const updatedCell = updatedExploration.view.cells.find(({ id }) => id === cell.id);
    if (updatedCell === undefined || !isRecordsLikeCell(updatedCell)) {
      throw new Error('Could not find updated cell');
    }
    const savedExploration = exploration;
    if (savedExploration === undefined) {
      throw new Error('Could not find saved exploration');
    }
    const savedCell = savedExploration.view.cells.find(({ id }) => id === cell.id);
    if (savedCell === undefined || !isRecordsLikeCell(savedCell)) {
      throw new Error('Could not find saved cell');
    }

    // TODO: if the parent pipeline changes then it should not skip
    if (isEqual(updatedCell.pipeline, savedCell.pipeline)) {
      return setExploration(updatedExploration);
    }

    const defaultVisualisation = generateVisualisation(
      ensureValidPipeline(savedCell.pipeline, {
        ...stateContext,
        exploration: savedExploration,
      }),
      stateContext,
    );

    setExploration({
      ...updatedExploration,
      view: {
        ...updatedExploration.view,
        cells: updatedExploration.view.cells.map((updatedCell) => {
          if (
            cell.id === updatedCell.id &&
            isRecordsLikeCell(updatedCell) &&
            updatedCell.visualisations !== undefined
          ) {
            const updatedRollbackIndex = (updatedCell.viewOptions as CellViewOptions | undefined)
              ?.rollback?.index;

            let finalUpdatedCell: Cell = updatedCell;
            if (
              updatedRollbackIndex !== undefined &&
              updatedRollbackIndex >= updatedCell.pipeline.operations.length
            ) {
              finalUpdatedCell = resetCellRollback(finalUpdatedCell);
            } else {
              const updatedPipeline =
                updatedRollbackIndex !== undefined
                  ? getPipelineWithRollback(updatedCell.pipeline, updatedRollbackIndex)
                  : updatedCell.pipeline;

              finalUpdatedCell = {
                ...finalUpdatedCell,
                visualisations: finalUpdatedCell.visualisations?.map((visualisation) =>
                  regenerateVisualisationIfNeeded({
                    visualisation,
                    defaultVisualisation,
                    nextDefaultVisualisation: generateVisualisation(
                      ensureValidPipeline(updatedPipeline, {
                        ...stateContext,
                        exploration: updatedExploration,
                      }),
                      stateContext,
                    ),
                  }),
                ),
              };
            }

            return finalUpdatedCell;
          }

          return updatedCell;
        }),
      },
    });
  };

  const wrappedSetCell = useCallback(
    (cell: Cell) => {
      setCellById(cell, cell.id);
    },
    [setCellById],
  );

  const wrappedSetQueryMeta = useCallback(
    (meta: QueryMeta | null) => setQueryMeta({ cellId: cell.id, meta }),
    [cell, setQueryMeta],
  );

  const applyCellViewOption = <T extends keyof CellViewOptions>(
    key: T,
    value: CellViewOptions[T] | undefined,
  ) => {
    wrappedSetCell(setViewOption(cell, key, value));
  };

  const getCellViewOption = useCallback(
    <T extends keyof CellViewOptions>(key: T) => get(cell.viewOptions, key, undefined),
    [cell.viewOptions],
  );

  const resetCellHeightAndShowTable = () => {
    wrappedSetCell(removeViewOptions(cell, [TableVisibilityKey, 'height']));
  };

  const resetMultiCellRowHeightAndShowTable = () => {
    const cellwithTable = removeViewOptions(cell, ['tableVisibility']);
    const cells = [...exploration.view.cells];
    cells[cellIndex] = cellwithTable;
    const explorationWithTable = {
      ...exploration,
      view: {
        ...exploration.view,
        cells,
      },
    };
    handleExplorationUpdate(updateCellRowHeight(explorationWithTable, cell.id, undefined));
  };

  const isCollapsed = useMemo(() => {
    const cellRow = getCellRow(getCellRows(exploration.view.cells), cellIndex);
    if (cellRow.length > 1 && enforcedRowCollapse) {
      return cellRow.every((cell) => get(cell.viewOptions, 'collapsed') === 'yes');
    }
    return getCellViewOption('collapsed') === 'yes';
  }, [getCellViewOption, cellIndex, enforcedRowCollapse, exploration.view.cells]);

  const toggleCollapsed = () => {
    if (isSelectedCell && !isCollapsed) {
      closeEditor();
    }

    if (enforcedRowCollapse) {
      // Toggle entire row on desktop
      return handleExplorationUpdate(updateCellRowCollapsed(exploration, cell.id, !isCollapsed));
    }

    const updatedCell: CellViewOptions = { collapsed: isCollapsed ? 'no' : 'yes' };
    if (!isCollapsed) {
      updatedCell.height = undefined;
    }
    wrappedSetCell(setViewOptions(cell, updatedCell));
  };

  const isTableVisible = getCellViewOption(TableVisibilityKey) !== 'hidden';
  const hideTable = () => applyCellViewOption(TableVisibilityKey, 'hidden');
  const showTable = () => {
    const isFromMultiCellRow =
      getCellRow(getCellRows(exploration.view.cells), cellIndex).length > 1;
    return isFromMultiCellRow
      ? resetMultiCellRowHeightAndShowTable()
      : resetCellHeightAndShowTable();
  };

  const tableMode = getCellViewOption(TableModeKey) === 'cards' ? 'cards' : 'table';

  const isPipelinePreviewVisible = getCellViewOption(PipelinePreviewKey) !== 'hidden';

  const isBlockTitleVisible = getCellViewOption(BlockTitleKey) !== 'hidden';

  const rollbackIndex = (cell.viewOptions as CellViewOptions | undefined)?.rollback?.index;
  const pipelineWithRollback = useMemo(
    () => (isCellWithPipeline(cell) ? getPipelineWithRollback(cell.pipeline, rollbackIndex) : null),
    [cell, rollbackIndex],
  );

  const setRollbackIndex = (index: number) => {
    if (!isCellWithPipeline(cell)) {
      return;
    }

    // Validate rollback index is within valid bounds
    const maxIndex = cell.pipeline.operations.length - 1;
    if (index < 0 || index > maxIndex) {
      return;
    }

    const shouldRegenerateVisualisations =
      isCellWithVisualisations(cell) &&
      cell.visualisations !== undefined &&
      cell.visualisations.length > 0;

    const viewOptions: Partial<CellViewOptions> =
      rollbackIndex === undefined && isCellWithVisualisations(cell)
        ? { rollback: { index, originalVisualisations: cell.visualisations } }
        : {
            rollback: {
              index,
              originalVisualisations: (cell.viewOptions as CellViewOptions | undefined)?.rollback
                ?.originalVisualisations,
            },
          };

    if (!shouldRegenerateVisualisations) {
      wrappedSetCell(setViewOptions(cell, viewOptions));
      return;
    }

    const currentPipeline =
      rollbackIndex === undefined
        ? cell.pipeline
        : getPipelineWithRollback(cell.pipeline, rollbackIndex);
    const nextPipeline = getPipelineWithRollback(cell.pipeline, index);

    const defaultVisualisation = generateVisualisation(
      ensureValidPipeline(currentPipeline, stateContext),
      stateContext,
    );
    const nextDefaultVisualisation = generateVisualisation(
      ensureValidPipeline(nextPipeline, stateContext),
      stateContext,
    );

    const visualisations = isCellWithVisualisations(cell) ? (cell.visualisations ?? []) : [];
    const regeneratedVisualisations = visualisations.map((visualisation) =>
      regenerateVisualisationIfNeeded({
        visualisation,
        defaultVisualisation,
        nextDefaultVisualisation,
      }),
    );

    wrappedSetCell({
      ...setViewOptions(cell, viewOptions),
      visualisations: regeneratedVisualisations,
    });
  };

  const clearRollback = () => {
    wrappedSetCell(resetCellRollback(cell));
  };

  const setVisualisations = (visualisations: Visualisation[]) => {
    if (!isRecordsLikeCell(cell)) {
      throw new Error(`Cannot set visualisations to cell of kind ${cell.kind}`);
    }

    const updatedCell =
      !isTableVisible && visualisations.length === 0
        ? removeViewOptions(cell, [TableVisibilityKey])
        : cell;

    wrappedSetCell({
      ...updatedCell,
      visualisations,
    });
  };

  const removeVisualisation = (idx: number) => {
    if (!isRecordsLikeCell(cell)) {
      return;
    }

    const visualisations = isCellWithVisualisations(cell) ? (cell.visualisations ?? []) : [];
    const { baseModelId, operations } = flattenPipeline(cell.pipeline, exploration);
    const analyticsProps = { baseModelId, operations, visualisations };
    const removingVisualisation = visualisations[idx];

    trackEvent('Visualisation Removed', {
      removingVisualisation,
      ...analyticsProps,
    });

    setVisualisations([...visualisations.slice(0, idx), ...visualisations.slice(idx + 1)]);
  };

  const setVisualisation = (idx: number, visualisation: Visualisation) => {
    if (
      !isRecordsLikeCell(cell) ||
      !isCellWithVisualisations(cell) ||
      cell.visualisations === undefined
    ) {
      return;
    }

    const visualisations = isCellWithVisualisations(cell) ? (cell.visualisations ?? []) : [];
    const { baseModelId, operations } = flattenPipeline(cell.pipeline, exploration);
    const analyticsProps = { baseModelId, operations, visualisations };

    trackEvent('Visualisation Changed', {
      visualisation,
      ...analyticsProps,
    });

    const updatedVisualisatios = [
      ...visualisations.slice(0, idx),
      visualisation,
      ...visualisations.slice(idx + 1),
    ];

    setVisualisations(updatedVisualisatios);
  };

  const addVisualization = (visualisation: Visualisation) => {
    if (!isRecordsLikeCell(cell)) {
      throw new Error(`Cannot add visualisation to cell of kind ${cell.kind}`);
    }
    setVisualisations([...(cell.visualisations ?? []), visualisation]);
  };

  const addDefaultVisualization = () => {
    if (!isRecordsLikeCell(cell)) {
      throw new Error(`Cannot add visualisation to cell of kind ${cell.kind}`);
    }
    addVisualization(
      generateVisualisation(
        ensureValidPipeline(pipelineWithRollback ?? cell.pipeline, {
          models,
          metrics,
          variables: getVariables(),
          exploration,
        }),
        { models, variables: getVariables(), metrics, exploration },
      ),
    );
  };

  const wrappedStartConversation = () => {
    if (isInvalidCell(cell)) {
      return;
    }

    setNewConversationSubjectId(cell.id);
  };

  const endConversation = () => {
    if (isConversationCell(cell)) {
      setNewConversationSubjectId(null);
      handleExplorationUpdate(
        deleteExplorationCell(
          setExplorationCells(
            exploration,
            exploration.view.cells.map((c) => {
              if (isConversationCell(c) || isInvalidCell(c) || isChatCell(c)) {
                return c;
              }
              if ('conversationId' in c) {
                const { conversationId, ...rest } = c;
                return rest;
              }
              return c;
            }),
          ),
          cell.id,
        ),
      );
    }
  };

  const getYaml = () => {
    if (!isCellWithPipeline(cell)) {
      return '';
    }

    return convertToYaml(
      convertKeysToSnakeCase({
        pipeline: dereferencePipeline(restoreInvalidOperations(cell.pipeline), exploration),
        ...(isCellWithVisualisations(cell) ? { visualisations: cell.visualisations } : {}),
      }),
    );
  };

  const copyCell = () => {
    const extractedCell = extractCellFromExploration(cell.id, exploration);

    if ('conversationId' in extractedCell.cell) {
      delete extractedCell.cell.conversationId;
    }

    persistCopiedCell(extractedCell);
  };

  // Pipeline operation handlers
  const warnIfOperationsRemoved = (updatedExploration: typeof exploration) => {
    const removedOperationsCount =
      countOperations(exploration) - countOperations(updatedExploration);

    if (removedOperationsCount > 0) {
      addToast(
        createBackNavigationToast(
          'Pipeline Step Deleted',
          'The change caused one or more pipeline steps to be deleted. To revert the changes you can',
          'click undo',
        ),
      );
    }
  };

  const addPipelineOperation = (atIndex: number, operation: PipelineOperation) => {
    if (!isRecordsLikeCell(cell)) {
      return;
    }
    const updatedExploration = addOperation(exploration, cell.id, atIndex, operation, stateContext);
    warnIfOperationsRemoved(updatedExploration);
    handleExplorationUpdate(updatedExploration);
  };

  const removePipelineOperation = (atIndex: number) => {
    if (!isRecordsLikeCell(cell)) {
      return;
    }
    const updatedExploration = removeOperation(exploration, cell.id, atIndex, stateContext);
    handleExplorationUpdate(updatedExploration);
  };

  const removeParentPipelineOperation = (atIndex: number) => {
    if (!isRecordsLikeCell(cell)) {
      return;
    }
    const updatedExploration = removeOperation(
      updateCellPipeline(
        exploration,
        cell.id,
        flattenPipeline(cell.pipeline, exploration),
        stateContext,
      ),
      cell.id,
      atIndex,
      stateContext,
    );
    handleExplorationUpdate(updatedExploration);
  };

  const updatePipelineOperation = (atIndex: number, operation: PipelineOperation) => {
    if (!isRecordsLikeCell(cell)) {
      return;
    }
    const updatedExploration = updateOperation(
      exploration,
      cell.id,
      atIndex,
      operation,
      stateContext,
    );
    warnIfOperationsRemoved(updatedExploration);
    handleExplorationUpdate(updatedExploration);
  };

  const updateParentPipelineOperation = (atIndex: number, operation: PipelineOperation) => {
    if (!isRecordsLikeCell(cell)) {
      return;
    }
    const updatedExploration = updateOperation(
      updateCellPipeline(
        exploration,
        cell.id,
        flattenPipeline(cell.pipeline, exploration),
        stateContext,
      ),
      cell.id,
      atIndex,
      operation,
      stateContext,
    );
    const removedOperationsCount =
      countOperations(exploration) - countOperations(updatedExploration);
    if (removedOperationsCount > 0) {
      addToast(
        createBackNavigationToast(
          'Pipeline Step Deleted and Pipeline Detached from Parent',
          'The change caused the pipeline to be detached from the parent and other pipeline step(s) to be deleted. To revert the changes you can',
          'click undo',
        ),
      );
    } else {
      addToast(
        createBackNavigationToast(
          'Pipeline Detached From Parent',
          'The change caused the pipeline to be detached from the parent. To revert the changes',
          'click undo',
        ),
      );
    }
    handleExplorationUpdate(updatedExploration);
  };

  const reorderPipelineOperation = (fromIndex: number, toIndex: number) => {
    if (!isRecordsLikeCell(cell)) {
      return;
    }
    const updatedExploration = moveOperation(
      exploration,
      cell.id,
      fromIndex,
      toIndex,
      stateContext,
    );
    warnIfOperationsRemoved(updatedExploration);
    handleExplorationUpdate(updatedExploration);
  };

  const disablePipelineOperation = (atIndex: number, disabled: boolean) => {
    if (!isRecordsLikeCell(cell)) {
      return;
    }
    const operations = cell.pipeline.operations.map((operation, index) =>
      index === atIndex ? { ...operation, disabled: disabled ? true : undefined } : operation,
    );
    handleExplorationUpdate(replaceOperations(exploration, cell.id, operations, stateContext));
  };

  const disableParentPipelineOperation = (atIndex: number, disabled: boolean) => {
    if (!isRecordsLikeCell(cell)) {
      return;
    }
    const flattenedPipeline = flattenPipeline(cell.pipeline, exploration);
    const updatedExploration = updateCellPipeline(
      exploration,
      cell.id,
      {
        ...flattenedPipeline,
        operations: flattenedPipeline.operations.map((operation, index) => {
          if (index === atIndex) {
            return { ...operation, disabled: disabled ? true : undefined };
          }
          return operation;
        }),
      },
      stateContext,
    );
    handleExplorationUpdate(updatedExploration);
    addToast(
      createBackNavigationToast(
        'Pipeline detached from parent',
        'The change caused the pipeline to be detached from the parent. To revert the changes',
        'click undo',
      ),
    );
  };

  const detachPipeline = () => {
    if (!isRecordsLikeCell(cell)) {
      return;
    }
    handleExplorationUpdate(
      updateCellPipeline(
        exploration,
        cell.id,
        flattenPipeline(cell.pipeline, exploration),
        stateContext,
      ),
    );
  };

  const changeBasePipeline = (newPipeline: Pipeline) => {
    if (!isRecordsLikeCell(cell)) {
      return;
    }
    // Add operations from the current pipeline to the new pipeline
    const updatedExploration = addOperations(
      updateCellPipeline(
        exploration,
        cell.id,
        {
          ...newPipeline,
          pipelineId: cell.pipeline.pipelineId, // Preserve the pipeline ID
        },
        stateContext,
      ),
      cell.id,
      newPipeline.operations.length,
      cell.pipeline.operations,
      stateContext,
    );
    handleExplorationUpdate(updatedExploration);
  };

  const addCombine = (items: CombineStep[]) => {
    const result = addCombineToExploration(exploration, items, cell.id);
    if (result === undefined) {
      return;
    }
    handleExplorationUpdate(ensureCellTabs(result.exploration, activeTabId));
    selectCell(result.cell.id);
    scrollToCell(result.cell.id);
  };

  const updateCombine = (items: CombineStep[]) => {
    const updatedExploration = updateCombineInExploration(
      exploration,
      items,
      cell.id,
      stateContext,
    );
    if (updatedExploration === undefined) {
      return;
    }
    handleExplorationUpdate(updatedExploration);
  };

  return (
    <ExplorationCellContext.Provider
      value={{
        cell,
        cellIndex,
        isSelectedCell,
        isConversationSubject: isConversationSubject(cell),
        isConversationPart: cellIsPartOfConversation,
        rowContainsConversationPart,
        pipelineWithRollback,
        setCell: wrappedSetCell,
        setQueryMeta: wrappedSetQueryMeta,
        setCellViewOption: applyCellViewOption,
        getCellViewOption,
        isCollapsed: isCollapsed && isCollapsible,
        isCollapsible,
        toggleCollapsed,
        addVisualization,
        addDefaultVisualization,
        startConversation: wrappedStartConversation,
        endConversation,
        setVisualisation,
        removeVisualisation,
        isTableVisible,
        hideTable,
        showTable,
        tableMode,
        isPipelinePreviewVisible,
        isBlockTitleVisible,
        getYaml,
        copyCell,
        setRollbackIndex,
        clearRollback,
        // Pipeline operation handlers
        addPipelineOperation,
        removePipelineOperation,
        removeParentPipelineOperation,
        updatePipelineOperation,
        updateParentPipelineOperation,
        reorderPipelineOperation,
        disablePipelineOperation,
        disableParentPipelineOperation,
        detachPipeline,
        changeBasePipeline,
        addCombine,
        updateCombine,
      }}>
      {children}
    </ExplorationCellContext.Provider>
  );
};
